import { LOCAL_STORAGE_TIMEZONE } from 'app/services/globals';
import { localStorageGet, localStorageSet } from 'app/shared/utils/window.util';
import { guessTimezone, isDaylightSavingsInEffect, timezones } from 'app/shared/utils/timezones.utils';
import moment from 'moment-timezone';

import { DateFilterType, IDateFilter } from './monitoring-models';

export type DateRangeManagerKey = 'orchestrate' | 'debugMonitoring' | 'sparkApplication' | 'sparkPool' | 'sqlRequest' | 'sqlApplication'
    | 'scopeJob' | 'scopePool' | 'sqlPool' | 'sqlStreamingRequest' | 'kqlRequest' | 'kustoPool' | 'linkTopic' | 'adfDataMapper' | 'dataflowHistory';

export class DateRangeManager {
    public static timeZone: string = DateRangeManager.getInitialTimeZone();
    public static sqlStandardDateRangeManagerKey: DateRangeManagerKey = 'sqlRequest';
    public static sqlStreamingDateRangeManagerKey: DateRangeManagerKey = 'sqlStreamingRequest';
    public static sparkPoolDateRangeManagerKey: DateRangeManagerKey = 'sparkPool';
    public static sqlPoolDateRangeManagerKey: DateRangeManagerKey = 'sqlPool';
    public static kqlJobDateRangeManagerKey: DateRangeManagerKey = 'kqlRequest';
    public static kustoPoolDateRangeManagerKey: DateRangeManagerKey = 'kustoPool';
    public static scopePoolDateRangeManagerKey: DateRangeManagerKey = 'scopePool';
    public static timeZoneAbbr: string = '';
    private static dateFilterMap: Map<DateRangeManagerKey, IDateFilter> = new Map();

    public static getDateFilter(key: DateRangeManagerKey, defaultDateRange: DateFilterType = 'last24hours'): IDateFilter {
        if (!this.dateFilterMap.has(key)) {
            switch (defaultDateRange) {
                case 'lastweek':
                    return this._getDefault7DaysFilter();
                case 'last30days':
                    return this._getDefault30DaysFilter();
                default:
                    return this._getDefault24HoursFilter();
            }
        }
        return this.dateFilterMap.get(key);
    }

    public static updateDataFilter(key: DateRangeManagerKey, value: IDateFilter): void {
        this.dateFilterMap.set(key, value);
    }

    public static getDateRange(key: DateRangeManagerKey): [Date, Date] {
        const dateFilter = this.getDateFilter(key);
        const definedRanges = this.getDateRanges();
        const definedRange = definedRanges[dateFilter.dateFilterType];
        if (definedRange) {
            [dateFilter.after, dateFilter.before] = [definedRange[0].toDate(), definedRange[1].toDate()];
        }
        return [dateFilter.after, dateFilter.before];
    }

    public static getRangesLabel(): { [id: string]: string } {
        return {
            last24hours: stringResources.Monitoring_Last24HourLabel,
            lastweek: stringResources.Monitoring_LastWeekLabel,
            last30days: stringResources.Monitoring_Last30DaysLabel,
            custom: stringResources.CustomRange
        };
    }

    public static getDateRanges(): { [id: string]: moment.Moment[]; } {
        const definedRanges: { [id: string]: moment.Moment[]; } = {};
        definedRanges['last24hours'] = [moment.tz(this.timeZone).subtract(24, 'hours'), moment.tz(this.timeZone)];
        definedRanges['lastweek'] = [moment.tz(this.timeZone).subtract(7, 'days'), moment.tz(this.timeZone)];
        definedRanges['last30days'] = [moment.tz(this.timeZone).subtract(30, 'days'), moment.tz(this.timeZone)];
        return definedRanges;
    }

    public static getTimeZones(): string[] {
        return [stringResources.Utc, stringResources.Local];
    }

    public static getInitialTimeZone(): string {
        const savedTimeZone = localStorageGet(LOCAL_STORAGE_TIMEZONE);
        return !!savedTimeZone ? savedTimeZone : guessTimezone();
    }

    public static getA365MonitoringDateRanges(): { [id: string]: moment.Moment[]; } {
        const definedRanges: { [id: string]: moment.Moment[]; } = {};
        definedRanges['last24hours'] = [moment.tz(this.timeZone).subtract(24, 'hours'), moment.tz(this.timeZone)];
        definedRanges['lastweek'] = [moment.tz(this.timeZone).subtract(7, 'days'), moment.tz(this.timeZone)];
        definedRanges['last30days'] = [moment.tz(this.timeZone).subtract(30, 'days'), moment.tz(this.timeZone)];
        definedRanges['custom'] = [undefined, undefined];
        return definedRanges;
    }

    public static updateTimeZone(timeZone: string): void {
        if (moment.tz.zone(timeZone)) {
            this.timeZone = timeZone;
            this.timeZoneAbbr = this.getTimezoneAbbr(timeZone);
        }
        localStorageSet(LOCAL_STORAGE_TIMEZONE, this.timeZone);
    }

    public static getTimezoneAbbr(timeZone: string): string {
        const tzEntry = timezones.find((zone) => zone.utc.includes(timeZone));
        return tzEntry ? tzEntry.abbr : '';
    }

    public static getTimeZoneOffset(timeZone: string): number {
        const tzEntry = timezones.find((zone) => zone.utc.includes(timeZone));
        return tzEntry.offset;
    }

    public static getTimeZoneLabel(timeZone: string): string {
        const tzEntry = timezones.find((zone) => zone.utc.includes(timeZone));
        return tzEntry ? tzEntry.text : '';
    }

    public static getTimeZoneLabelConsiderLocalTime(): string {
        const today = new Date();
        let localTimeZoneOffset = 0 - (today.getTimezoneOffset() / 60);
        const tzEntry = timezones.find((zone) => zone.utc.includes(this.timeZone));
        // ITimeZone offset will be off by 1 if daylight savings has ended
        if (!isDaylightSavingsInEffect(tzEntry)) {
            localTimeZoneOffset -= 1;
        }
        return tzEntry ? tzEntry.offset === localTimeZoneOffset ? stringResources.LocalTime : tzEntry.text : '';
    }

    /* Gets the timezone offset different considering the daylight savings.
     Example: 1) PST is UTC-7 when in DST period  2) PST is UTC-8 when not in DST period. */
    public static getTimeZoneOffsetConsiderDST(timeZone: string): number {
        const tzEntry = timezones.find((zone) => zone.utc.includes(timeZone));
        const dstInEffect = isDaylightSavingsInEffect(tzEntry);
        return tzEntry ? (tzEntry.isdst && !dstInEffect ? tzEntry.offset - 1 : tzEntry.offset) : 0;
    }

    public static isDayLightSavingsObserved(timeZone: string): boolean {
        const tzEntry = timezones.find((zone) => zone.utc.includes(timeZone));
        return tzEntry ? tzEntry.isdst : false;
    }

    public static getTimeZoneFromAbbr(abbr: string): string {
        const tzEntry = timezones.find((zone) => zone.abbr === abbr);
        return tzEntry ? tzEntry.utc[0] : '';
    }

    private static _getDefault24HoursFilter(): IDateFilter {
        return {
            label: stringResources.Monitoring_Last24HourLabel,
            dateFilterType: 'last24hours',
            after: moment.tz(DateRangeManager.timeZone).subtract(24, 'hours').toDate(),
            before: moment.tz(DateRangeManager.timeZone).toDate()
        };
    }

    private static _getDefault7DaysFilter(): IDateFilter {
        return {
            label: stringResources.Monitoring_LastWeekLabel,
            dateFilterType: 'lastweek',
            after: moment.tz(DateRangeManager.timeZone).subtract(7, 'days').toDate(),
            before: moment.tz(DateRangeManager.timeZone).toDate()
        };
    }

    private static _getDefault30DaysFilter(): IDateFilter {
        return {
            label: stringResources.Monitoring_Last30DaysLabel,
            dateFilterType: 'last30days',
            after: moment.tz(DateRangeManager.timeZone).subtract(30, 'days').toDate(),
            before: moment.tz(DateRangeManager.timeZone).toDate()
        };
    }
}
